import React, { memo, useEffect, useState } from '../../../../lib/teact/teact';

import type {
  ApiWalletTransactionAttachment,
  ApiWalletTransactionAttachmentContent,
  ApiWalletTransactionDraftAttachment,
} from '../../../../api/types';

import buildClassName from '../../../../util/buildClassName';
import { getTransactionAttachmentFileType } from './transactionMetadataFiles';

import useLastCallback from '../../../../hooks/useLastCallback';

import styles from './TransactionMetadata.module.scss';

import fileIcons from '../../../../assets/transaction-file-icons.svg';

export type AttachmentContentLoader = (
  attachment: ApiWalletTransactionAttachment,
) => Promise<ApiWalletTransactionAttachmentContent | undefined>;

type Props = {
  attachment: ApiWalletTransactionAttachment | ApiWalletTransactionDraftAttachment;
  onLoadContent?: AttachmentContentLoader;
  previewRevision?: number;
};

export default memo(({ attachment, onLoadContent, previewRevision }: Props) => {
  const fileType = getTransactionAttachmentFileType(attachment.content_type, attachment.file_name);
  const content = 'content' in attachment ? attachment.content : undefined;
  const attachmentId = 'id' in attachment ? attachment.id : attachment.client_id;
  const contentType = attachment.content_type;
  const previewKey = `${attachmentId}:${contentType}`;
  const [thumbnail, setThumbnail] = useState<{ key: string; url: string }>();
  const [failedUrl, setFailedUrl] = useState<string>();

  const loadContent = useLastCallback(async () => {
    return 'content' in attachment ? attachment.content : (await onLoadContent?.(attachment))?.content;
  });

  useEffect(() => {
    if (fileType.kind !== 'image') return undefined;
    let isCancelled = false;
    let objectUrl: string | undefined;
    void loadContent().then((bytes) => {
      if (isCancelled || !bytes) return;
      objectUrl = URL.createObjectURL(new Blob([bytes], { type: contentType }));
      setThumbnail({ key: previewKey, url: objectUrl });
    }).catch(() => {
      // A failed thumbnail must not interrupt editing or trigger authorization.
    });
    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [content, contentType, fileType.kind, loadContent, onLoadContent, previewKey, previewRevision]);

  const imageUrl = thumbnail?.key === previewKey && thumbnail.url !== failedUrl ? thumbnail.url : undefined;
  return (
    <span className={buildClassName(styles.attachmentPreview, styles[fileType.kind])} data-file-type={fileType.kind}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className={styles.attachmentThumbnail}
          onError={() => setFailedUrl(imageUrl)}
        />
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
          <use href={`${fileIcons}#${fileType.icon}`} />
        </svg>
      )}
    </span>
  );
});
